import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addressBookApi, type AddressEntry } from './api.ts';
import { dv } from '../../i18n/dv.ts';

// The recipient snapshot an editor stores against a letter — either copied
// from an address-book entry (by picking one) or typed ad-hoc for a
// recipient not in the book. Either way the letter keeps its own copy, so
// later edits/deletes to the address book don't retroactively change past
// letters.
export interface AddressSnapshot {
  officeName: string;
  addresseePerson?: string;
  address?: string;
  atoll?: string;
  island?: string;
}

const EMPTY_SNAPSHOT: AddressSnapshot = { officeName: '' };

function entryToSnapshot(e: AddressEntry): AddressSnapshot {
  return {
    officeName: e.officeName,
    addresseePerson: e.addresseePerson ?? undefined,
    address: e.address ?? undefined,
    atoll: e.atoll ?? undefined,
    island: e.island ?? undefined,
  };
}

export interface AddressPickerProps {
  value: AddressSnapshot;
  onChange: (value: AddressSnapshot) => void;
}

// Reusable recipient picker for the letter editor (Task 10): a dropdown of
// saved address-book entries, or a manual free-text mode for a one-off
// recipient. Picking an entry emits a plain-data snapshot (not the entry id)
// so the caller can store it directly on the letter.
export function AddressPicker({ value, onChange }: AddressPickerProps): JSX.Element {
  const { data: entries } = useQuery({ queryKey: ['addressBook'], queryFn: addressBookApi.list });
  const list = entries ?? [];
  const [manual, setManual] = useState(() => !list.some((e) => e.officeName === value.officeName));
  const [selectedId, setSelectedId] = useState('');

  const set = (patch: Partial<AddressSnapshot>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setManual(false)}
          className={!manual ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
        >
          {dv.addressBook.pickFromBook}
        </button>
        <button
          type="button"
          onClick={() => setManual(true)}
          className={manual ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
        >
          {dv.addressBook.manualEntry}
        </button>
      </div>

      {!manual && (
        <select
          className="input w-full"
          value={selectedId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedId(id);
            const entry = list.find((x) => x.id === id);
            onChange(entry ? entryToSnapshot(entry) : EMPTY_SNAPSHOT);
          }}
        >
          <option value="">{dv.addressBook.selectPlaceholder}</option>
          {list.map((e) => (
            <option key={e.id} value={e.id}>{e.officeName}</option>
          ))}
        </select>
      )}

      {manual && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm sm:col-span-2">
            <span className="field-label">{dv.addressBook.officeName} *</span>
            <input
              className="input w-full"
              value={value.officeName}
              onChange={(e) => set({ officeName: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="field-label">{dv.addressBook.addresseePerson}</span>
            <input
              className="input w-full"
              value={value.addresseePerson ?? ''}
              onChange={(e) => set({ addresseePerson: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="field-label">{dv.addressBook.address}</span>
            <input
              className="input w-full"
              value={value.address ?? ''}
              onChange={(e) => set({ address: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="field-label">{dv.addressBook.atoll}</span>
            <input
              className="input w-full"
              value={value.atoll ?? ''}
              onChange={(e) => set({ atoll: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="field-label">{dv.addressBook.island}</span>
            <input
              className="input w-full"
              value={value.island ?? ''}
              onChange={(e) => set({ island: e.target.value })}
              maxLength={200}
            />
          </label>
        </div>
      )}
    </div>
  );
}
